import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  releaseNotesKommandostandSchema,
  whatsNewFensterSchema,
  whatsNewListeSchema,
  type ReleaseNotesKommandostand,
  type WhatsNewFenster,
  type WhatsNewListe,
} from "@shared/schemas/releaseNotes";

import { apiGet, apiPatch, apiPost } from "@/lib/api/client";
import { useAuth } from "@/lib/auth/AuthContext";
import { queryKeys } from "@/lib/query/keys";

// ---------------------------------------------------------------------------
// useWhatsNew — «Was ist neu» (NEU-1): Fenster, Verstanden, Liste, Kommandostand.
// ---------------------------------------------------------------------------
//
// Das Fenster fragt der Server: er kennt die Gesehen-Marke und die laufende
// Version. Der Hook fragt beim Oeffnen der App und — anders als der Rest der
// App — auch beim Zurueckkommen in den Tab (`refetchOnWindowFocus`), damit
// die installierte App am Handy das Fenster ohne Neuladen bekommt; die
// Frische von 30 Minuten haelt das bei einem Klick pro Tab-Wechsel still.
//
// In Vertretung wird gar nicht gefragt (`enabled`): der Server antwortet
// dann ohnehin «vertretung», aber ein Aufruf, der nichts zeigen darf, muss
// nicht laufen.

interface ApiOk {
  ok: true;
  data: unknown;
}

const FENSTER_STALE_MS = 30 * 60_000;

export function useWhatsNewFenster() {
  const { user, actedAsUserId } = useAuth();
  return useQuery({
    queryKey: queryKeys.releaseNotes.fenster(),
    queryFn: async (): Promise<WhatsNewFenster> => {
      const res = await apiGet<ApiOk>("/api/release-notes");
      return whatsNewFensterSchema.parse(res.data);
    },
    enabled: user !== null && actedAsUserId === null,
    staleTime: FENSTER_STALE_MS,
    refetchOnWindowFocus: true,
  });
}

function fehlerText(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/**
 * «Verstanden»: schreibt die Marke und nimmt das Fenster SOFORT aus dem
 * Cache (optimistisch) — der Dialog verschwindet mit dem Klick, nicht mit
 * der Antwort. Scheitert der Server, sagt ein Toast, dass das Fenster beim
 * naechsten Oeffnen wiederkommt.
 */
export function useWhatsNewVerstanden() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { version: string }) =>
      apiPost<{ ok: true; data: { seenVersion: string } }>(
        "/api/release-notes/seen",
        { version: args.version },
      ),
    onMutate: (args) => {
      queryClient.setQueryData<WhatsNewFenster>(
        queryKeys.releaseNotes.fenster(),
        (alt) =>
          alt
            ? {
                ...alt,
                mode: "leer",
                seenVersion: args.version,
                meldungen: [],
                neu: [],
                weitere: 0,
              }
            : alt,
      );
    },
    onError: (err) => {
      toast.error(
        `«Verstanden» konnte nicht gespeichert werden — das Fenster kommt beim nächsten Öffnen wieder: ${fehlerText(err)}`,
      );
    },
  });
}

export function useWhatsNewListe() {
  return useQuery({
    queryKey: queryKeys.releaseNotes.liste(),
    queryFn: async (): Promise<WhatsNewListe> => {
      const res = await apiGet<ApiOk>("/api/release-notes?scope=liste");
      return whatsNewListeSchema.parse(res.data);
    },
    staleTime: 60_000,
  });
}

export function useReleaseNotesKommandostand() {
  return useQuery({
    queryKey: queryKeys.releaseNotes.kommandostand(),
    queryFn: async (): Promise<ReleaseNotesKommandostand> => {
      const res = await apiGet<ApiOk>("/api/release-notes?scope=kommandostand");
      return releaseNotesKommandostandSchema.parse(res.data);
    },
    staleTime: 60_000,
  });
}

/** Ausblenden / wieder einblenden — weich, reversibel (Prinzip 14). */
export function useReleaseNoteAusblenden() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; hidden: boolean }) =>
      apiPatch<{ ok: true }>("/api/release-notes", args),
    onSuccess: (_data, args) => {
      toast.success(
        args.hidden ? "Zeile ausgeblendet." : "Zeile wieder eingeblendet.",
      );
      void queryClient.invalidateQueries({
        queryKey: queryKeys.releaseNotes.kommandostand(),
      });
    },
    onError: (err) => {
      toast.error(`Zeile konnte nicht geändert werden: ${fehlerText(err)}`);
    },
  });
}
